import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type { LessonLog, MakeupSchedule } from '@/types';
import Field, { inputClass } from '@/components/base/Field';
import { formatDate } from '@/lib/datetime';

interface MakeupSectionProps {
  studentId: string;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const REASONS = ['결석 보충', '추가 수업', '진도 보충', '시험 대비', '기타'];

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function weekdayLabel(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return WEEKDAYS[d.getDay()];
}

export default function MakeupSection({ studentId }: MakeupSectionProps) {
  const [schedules, setSchedules] = useState<MakeupSchedule[]>([]);
  const [lessonMap, setLessonMap] = useState<Record<string, LessonLog>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [date, setDate] = useState(todayStr());
  const [reason, setReason] = useState(REASONS[0]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('makeup_schedules')
        .select('*')
        .eq('student_id', studentId)
        .order('scheduled_date', { ascending: false });
      if (fetchError) throw fetchError;
      const list = (data as MakeupSchedule[]) ?? [];
      setSchedules(list);

      const lessonIds = list
        .map((s) => s.lesson_log_id)
        .filter((id): id is string => Boolean(id));
      if (lessonIds.length > 0) {
        const { data: logs, error: logError } = await supabase
          .from('lesson_logs')
          .select('*')
          .in('id', lessonIds);
        if (logError) throw logError;
        const map: Record<string, LessonLog> = {};
        ((logs as LessonLog[]) ?? []).forEach((l) => {
          map[l.id] = l;
        });
        setLessonMap(map);
      } else {
        setLessonMap({});
      }
    } catch {
      setError('보충 일정을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setDate(todayStr());
    setReason(REASONS[0]);
    setNote('');
    setShowForm(false);
  };

  const addSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!date) {
      setError('보충 일자를 선택해주세요.');
      return;
    }
    setSaving(true);
    try {
      const { data, error: insertError } = await supabase
        .from('makeup_schedules')
        .insert({
          student_id: studentId,
          scheduled_date: date,
          reason,
          note: note.trim(),
        })
        .select();
      if (insertError) throw insertError;
      const created = (data as MakeupSchedule[] | null)?.[0];
      if (created) {
        setSchedules((prev) =>
          [...prev, created].sort((a, b) => (a.scheduled_date < b.scheduled_date ? 1 : -1)),
        );
      }
      resetForm();
    } catch {
      setError('보충 일정 등록에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  const removeSchedule = async (id: string) => {
    setError('');
    try {
      const { error: deleteError } = await supabase
        .from('makeup_schedules')
        .delete()
        .eq('id', id);
      if (deleteError) throw deleteError;
      setSchedules((prev) => prev.filter((s) => s.id !== id));
      setPendingDelete(null);
    } catch {
      setError('삭제에 실패했어요.');
    }
  };

  const upcomingCount = schedules.filter(
    (s) => !s.lesson_log_id && s.scheduled_date >= todayStr(),
  ).length;

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-calendar-schedule-line"></i>
            </span>
            보충 수업
          </h2>
          <p className="mt-1 text-xs text-foreground-500">
            보충 일자를 따로 잡아두고, 보충 수업이 끝나면 수업일지를 작성할 수 있어요. 예정{' '}
            <strong className="font-semibold text-foreground-700">{upcomingCount}</strong>건
          </p>
        </div>
        <button
          type="button"
          onClick={() => (showForm ? resetForm() : setShowForm(true))}
          className="flex items-center gap-1.5 rounded-md bg-accent-500 px-3.5 py-2 text-sm font-semibold text-background-50 transition hover:bg-accent-600 whitespace-nowrap"
        >
          <i className={showForm ? 'ri-close-line' : 'ri-add-line'}></i>
          {showForm ? '닫기' : '보충 일자 잡기'}
        </button>
      </div>

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={addSchedule}
          className="mt-4 rounded-lg border border-background-200 bg-background-100 p-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="보충 일자" required>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="보충 사유">
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className={inputClass}
              >
                {REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="메모" hint="보충 범위나 준비물 등을 적어두세요.">
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={200}
                  placeholder="예: 지난주 결석 보충 - 이차함수 그래프"
                  className={inputClass}
                />
              </Field>
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={resetForm}
              className="rounded-md border border-background-300 px-4 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 whitespace-nowrap"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-accent-600 disabled:opacity-50 whitespace-nowrap"
            >
              {saving ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-check-line"></i>
              )}
              일정 추가
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-sm text-foreground-500">
          <i className="ri-loader-4-line mr-2 animate-spin"></i>
          불러오는 중...
        </div>
      ) : schedules.length === 0 ? (
        <p className="mt-5 rounded-lg border border-dashed border-background-300 py-10 text-center text-sm text-foreground-500">
          아직 잡아둔 보충 일자가 없어요.
        </p>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          {schedules.map((s) => {
            const linked = s.lesson_log_id ? lessonMap[s.lesson_log_id] : null;
            const done = Boolean(linked);
            return (
              <article
                key={s.id}
                className="rounded-xl border border-background-200 bg-background-50 p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <div
                      className={`flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-lg ${
                        done
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-accent-100 text-accent-700'
                      }`}
                    >
                      <i className={done ? 'ri-check-double-line' : 'ri-calendar-schedule-line'}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-bold text-foreground-950">
                          {formatDate(s.scheduled_date)}
                          {weekdayLabel(s.scheduled_date) && (
                            <span className="ml-1 text-xs font-medium text-foreground-500">
                              ({weekdayLabel(s.scheduled_date)})
                            </span>
                          )}
                        </p>
                        {done ? (
                          <span className="flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-800">
                            <i className="ri-check-line"></i>
                            수업일지 작성 완료
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-800">
                            <i className="ri-time-line"></i>
                            보충 예정
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-600">
                        <span className="flex items-center gap-1">
                          <i className="ri-bookmark-line text-accent-600"></i>
                          {s.reason || '보충'}
                        </span>
                        {s.note && (
                          <span className="flex items-center gap-1">
                            <i className="ri-sticky-note-line text-foreground-400"></i>
                            {s.note}
                          </span>
                        )}
                      </div>
                      {linked?.lesson_scope && (
                        <p className="mt-1.5 line-clamp-2 text-xs text-foreground-500">
                          수업 범위 · {linked.lesson_scope}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {linked ? (
                      <Link
                        to={`/lessons/${linked.id}/edit`}
                        className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 whitespace-nowrap"
                      >
                        <i className="ri-edit-line"></i>
                        수업일지 보기
                      </Link>
                    ) : (
                      <Link
                        to={`/lessons/new?student=${studentId}&makeup=1&date=${s.scheduled_date}&schedule=${s.id}`}
                        className="flex items-center gap-1 rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-background-50 transition hover:bg-primary-600 whitespace-nowrap"
                      >
                        <i className="ri-edit-box-line"></i>
                        수업일지 작성
                      </Link>
                    )}

                    {pendingDelete === s.id ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => removeSchedule(s.id)}
                          className="rounded-md bg-accent-500 px-2.5 py-1.5 text-xs font-semibold text-background-50 transition hover:bg-accent-600 whitespace-nowrap"
                        >
                          삭제
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDelete(null)}
                          className="rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 whitespace-nowrap"
                        >
                          취소
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPendingDelete(s.id)}
                        aria-label="보충 일정 삭제"
                        className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-400 transition hover:bg-background-200 hover:text-accent-700"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}